import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * 画像を大きく見る窓（#124 の 3a の続き、docs/50 §7。10/4 射場さん「画像をクリックしたら大きな画面も。サムネイルでは不明瞭」）。
 *   画面いっぱいの暗い幕に、画面に合わせた大きさで出す。画像か「原寸で表示」で原寸（はみ出す分はスクロール）。「保存」は名前つきで保存（download）。
 *   閉じるのは「閉じる」・Esc・幕。2枚以上なら ‹ › と ← →。閉じたら押した見本に戻る。写しに入らない（data-nocapture）
 *   使い方：const v = useImageViewer(); …onClick={() => v.open(list, i)} … {v.node}
 */
export type ViewImage = { src: string; name: string };

export function useImageViewer() {
  const [state, setState] = useState<{ list: ViewImage[]; index: number } | null>(null);
  const open = (list: ViewImage[], index = 0) => list.length > 0 && setState({ list, index: Math.min(Math.max(0, index), list.length - 1) });
  const node = state ? <ImageViewer list={state.list} index={state.index} onClose={() => setState(null)} /> : null;
  return { open, node };
}

export function ImageViewer({ list, index, onClose }: { list: ViewImage[]; index: number; onClose: () => void }) {
  const [i, setI] = useState(index);
  const [actual, setActual] = useState(false);
  const closeBtn = useRef<HTMLButtonElement>(null);
  const many = list.length > 1;
  const go = (d: number) => {
    setActual(false);
    setI((x) => (x + d + list.length) % list.length);
  };
  const goRef = useRef(go);
  goRef.current = go;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const back = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeBtn.current?.focus();
    // 後ろの窓（「？」の窓など）の Esc より先に受け取る
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        closeRef.current();
      } else if (many && (e.key === "ArrowRight" || e.key === "ArrowLeft")) {
        e.preventDefault();
        e.stopPropagation();
        goRef.current(e.key === "ArrowRight" ? 1 : -1);
      }
    };
    window.addEventListener("keydown", onKey, true);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = overflow;
      back?.focus();
    };
  }, [many]);

  const cur = list[i] ?? list[0]!;
  const onBack = (e: React.MouseEvent) => e.target === e.currentTarget && onClose();
  return createPortal(
    <div className="imgview" role="dialog" aria-modal="true" aria-label={cur.name} data-nocapture data-testid="image-viewer" onMouseDown={onBack}>
      <div className="ivbar">
        <span className="ivname" data-testid="image-viewer-name">
          {cur.name}
          {many ? `（${i + 1}／${list.length}）` : ""}
        </span>
        <button type="button" className="ivbtn" aria-pressed={actual} onClick={() => setActual((a) => !a)} data-testid="image-viewer-actual">
          {actual ? "全体表示" : "原寸で表示"}
        </button>
        <a className="ivbtn" href={cur.src} download={cur.name} data-testid="image-viewer-save">
          保存
        </a>
        <a className="ivbtn" href={cur.src} target="_blank" rel="noopener noreferrer">
          別タブで表示
        </a>
        <button type="button" ref={closeBtn} className="ivbtn" onClick={onClose} data-testid="image-viewer-close">
          閉じる
        </button>
      </div>
      <div className={"ivstage" + (actual ? " actual" : "")} onMouseDown={onBack}>
        <img src={cur.src} alt={cur.name} onClick={() => setActual((a) => !a)} data-testid="image-viewer-img" />
      </div>
      {many ? (
        <>
          <button type="button" className="ivnav ivprev" aria-label="前の画像" onClick={() => go(-1)}>
            ‹
          </button>
          <button type="button" className="ivnav ivnext" aria-label="次の画像" onClick={() => go(1)}>
            ›
          </button>
        </>
      ) : null}
    </div>,
    document.body,
  );
}
